import type { UserDto, UserRole } from 'codesearch-backend'
import { useQuery, useQueryClient } from '@tanstack/solid-query'
import { For, Show } from 'solid-js'
import { toast } from 'solid-sonner'
import { authMe } from '../../store/auth.ts'
import { trpc } from '../../trpc.ts'
import { Button } from '../ui/Button.tsx'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover.tsx'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select.tsx'
import { Table, TableCell, TableRow } from '../ui/Table.tsx'

const ROLES: UserRole[] = ['viewer', 'admin']

function RepoAccess(props: { user: UserDto, repos: string[], onChange: (repos: string[]) => void }) {
  const allowed = () => props.user.allowedRepos
  const unrestricted = () => allowed().length === 0

  function toggle(repo: string) {
    const next = allowed().includes(repo) ? allowed().filter(it => it !== repo) : [...allowed(), repo]
    props.onChange(next)
  }

  return (
    <Popover>
      <PopoverTrigger
        as={Button<'button'>}
        variant="outline"
        size="sm"
        class="h-7 gap-1 text-xs font-normal"
        disabled={props.user.role === 'admin'}
      >
        <div class="i-lucide-folder-lock size-3" />
        <Show when={props.user.role === 'admin'} fallback={unrestricted() ? 'all indexes' : `${allowed().length} index(es)`}>
          all (admin)
        </Show>
      </PopoverTrigger>
      <PopoverContent class="w-56 p-1">
        <div class="px-2 py-1 text-xs text-muted-foreground">
          nothing checked = every index
        </div>
        <For each={props.repos} fallback={<div class="px-2 py-1 text-xs text-muted-foreground">no indexes</div>}>
          {repo => (
            <div
              class="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-accent/75"
              onClick={() => toggle(repo)}
            >
              <div
                class={allowed().includes(repo)
                  ? 'i-lucide-check-square size-3.5 shrink-0'
                  : 'i-lucide-square size-3.5 shrink-0 text-muted-foreground'}
              />
              <span class="truncate font-mono">{repo}</span>
            </div>
          )}
        </For>
      </PopoverContent>
    </Popover>
  )
}

export function UsersPage() {
  const queryClient = useQueryClient()

  const listQuery = useQuery(() => ({ queryKey: ['users'], queryFn: () => trpc.users.list.query() }))
  const reposQuery = useQuery(() => ({ queryKey: ['allRepos'], queryFn: () => trpc.search.allRepos.query() }))

  const users = () => listQuery.data ?? []

  async function run(action: () => Promise<unknown>) {
    try {
      await action()
      await queryClient.invalidateQueries({ queryKey: ['users'] })
    } catch (err) {
      toast.error((err as Error).message)
    }
  }

  return (
    <Show when={users().length} fallback={<p class="text-sm text-muted-foreground">No users yet</p>}>
      <Table columns="minmax(0, 1fr) 7rem auto auto auto">
        <TableRow class="text-xs text-muted-foreground font-medium">
          <TableCell>Name</TableCell>
          <TableCell>Role</TableCell>
          <TableCell>Indexes</TableCell>
          <TableCell class="text-center">State</TableCell>
          <TableCell />
        </TableRow>
        <For each={users()}>
          {(user: UserDto) => (
            <TableRow class="items-center" classList={{ 'opacity-50': user.disabled }}>
              <TableCell class="truncate text-sm">
                {user.displayName}
                <Show when={user.id === authMe()?.userId}>
                  <span class="ml-1 text-xs text-muted-foreground">(you)</span>
                </Show>
              </TableCell>

              <TableCell>
                <Select
                  value={user.role}
                  options={ROLES}
                  onChange={role => role && run(() => trpc.users.setRole.mutate({ id: user.id, role }))}
                  itemComponent={itemProps => <SelectItem item={itemProps.item}>{itemProps.item.rawValue}</SelectItem>}
                >
                  <SelectTrigger class="h-7 text-xs">
                    <SelectValue<UserRole>>{state => state.selectedOption()}</SelectValue>
                  </SelectTrigger>
                  <SelectContent />
                </Select>
              </TableCell>

              <TableCell>
                <RepoAccess
                  user={user}
                  repos={reposQuery.data ?? []}
                  onChange={repos => run(() => trpc.users.setAllowedRepos.mutate({ id: user.id, repos }))}
                />
              </TableCell>

              <TableCell class="flex justify-center">
                <Button
                  variant="ghost"
                  size="sm"
                  class="h-7 text-xs text-muted-foreground"
                  onClick={() => run(() => trpc.users.setDisabled.mutate({ id: user.id, disabled: !user.disabled }))}
                >
                  {user.disabled ? 'enable' : 'disable'}
                </Button>
              </TableCell>

              <TableCell class="flex justify-end">
                <Button
                  variant="ghostDestructive"
                  size="sm"
                  class="h-7 text-xs"
                  onClick={() => run(() => trpc.users.remove.mutate({ id: user.id }))}
                >
                  delete
                </Button>
              </TableCell>
            </TableRow>
          )}
        </For>
      </Table>
    </Show>
  )
}
